// deps
import React, { useMemo } from "react";

// internal-deps
import { useEffectOnce } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// constants
import { TEXTS } from "@payment-ui/constants/Wallet";

// test-ids
import { WalletTestIds } from "@swiggy-private/payment-test-ids";

// styles
import { Container, AddMoneyInput, ButtonWrapper } from "./styles/AddMoney.styled";

export interface WalletAddMoneyProps {
    additionalBalanceNeeded: number | string;
    onClick: (val: number) => void;
    addMoneyImpressionEvent: VoidFunction;
    walletTestIds: WalletTestIds;
}

function WalletAddMoney({
    onClick,
    additionalBalanceNeeded,
    addMoneyImpressionEvent,
    walletTestIds,
}: WalletAddMoneyProps): React.ReactElement {
    const [value, setValue] = React.useState<string | number>(Number(additionalBalanceNeeded) || 0);
    const isAmountInsufficient = useMemo(() => value < additionalBalanceNeeded, [additionalBalanceNeeded, value]);
    const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(Number(e.target.value) || "");
    }, []);

    const onBtnClick = React.useCallback(() => {
        onClick(Number(value) || 0);
    }, [onClick, value]);

    const onAddMoneyClick = React.useCallback((e: React.MouseEvent): void => {
        e.stopPropagation();
    }, []);

    useEffectOnce(() => {
        addMoneyImpressionEvent();
    });

    return (
        <Container onClick={onAddMoneyClick}>
            <AddMoneyInput
                onChange={onChange}
                $hasError={isAmountInsufficient}
                type="tel"
                autoComplete="off"
                value={value}
                data-testid={walletTestIds.ADD_MONEY_INPUT}
            />
            <ButtonWrapper>
                <SuperButton
                    variant="fullwidth"
                    buttonType="specialPositive"
                    buttonSize="medium"
                    onClick={onBtnClick}
                    isDisabled={isAmountInsufficient}
                    data-testid={walletTestIds.ADD_MONEY_BUTTON}
                >
                    {TEXTS.ADD_MONEY}
                </SuperButton>
            </ButtonWrapper>
        </Container>
    );
}

export default React.memo(WalletAddMoney);
